"use client";

import { useId, useState, type ReactNode } from "react";
import { langProps, type EditLang } from "./locale";

// The admin dashboard's building blocks. Brand palette; every control is at least 44px tall for touch.

export const INPUT =
  "w-full rounded-lg border border-midnight/20 bg-offwhite px-3.5 py-2.5 text-base text-midnight placeholder:text-midnight/60 focus:border-teal focus:ring-2 focus:ring-teal/20 focus:outline-none";
export const BTN =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 font-mono text-[0.7rem] font-medium tracking-[0.15em] whitespace-nowrap uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50";
export const BTN_PRIMARY = `${BTN} bg-midnight text-offwhite hover:bg-teal`;
export const BTN_GHOST = `${BTN} border border-midnight/20 text-midnight hover:border-teal hover:text-teal`;
export const BTN_DANGER = `${BTN} border border-red-700/30 text-red-800 hover:bg-red-700 hover:text-white`;
export const CARD = "rounded-2xl border border-midnight/10 bg-sand p-4 sm:p-6";

export function Field({ label, hint, count, max, children }: { label: string; hint?: ReactNode; count?: number; max?: number; children: (id: string) => ReactNode }) {
  const id = useId();
  const over = max !== undefined && count !== undefined && count > max;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-bold">
          {label}
        </label>
        {max !== undefined && count !== undefined && (
          <span className={`font-mono text-xs ${over ? "font-bold text-red-800" : "text-midnight/70"}`}>
            {count}/{max}
          </span>
        )}
      </div>
      {children(id)}
      {hint && <p className="text-sm text-midnight/70">{hint}</p>}
    </div>
  );
}

export function TextField({ label, hint, value, onChange, max, placeholder, type = "text", required, lang }: {
  label: string;
  /** The language of the text (Arabic is typed right to left). */
  lang?: EditLang;
  hint?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  max?: number;
  placeholder?: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <Field label={label + (required ? " *" : "")} hint={hint} count={max ? value.length : undefined} max={max}>
      {(id) => <input id={id} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={INPUT} {...(lang && langProps(lang))} />}
    </Field>
  );
}

export function TextArea({ label, hint, value, onChange, max, rows = 3, placeholder, required, lang }: {
  label: string;
  lang?: EditLang;
  hint?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  max?: number;
  rows?: number;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <Field label={label + (required ? " *" : "")} hint={hint} count={max ? value.length : undefined} max={max}>
      {(id) => <textarea id={id} rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={`${INPUT} resize-y leading-relaxed`} {...(lang && langProps(lang))} />}
    </Field>
  );
}

export function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: { value: string; label: string }[] }) {
  return (
    <Field label={label}>
      {(id) => (
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={INPUT}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/** Draft / Published switch. Drafts never appear on the website. */
export function StatusSwitch({ value, onChange }: { value: "published" | "draft"; onChange: (value: "published" | "draft") => void }) {
  return (
    <div role="radiogroup" aria-label="Status" className="inline-flex rounded-lg border border-midnight/20 p-1">
      {(["published", "draft"] as const).map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={value === s}
          onClick={() => onChange(s)}
          className={`min-h-10 rounded-md px-4 font-mono text-[0.7rem] tracking-[0.15em] uppercase transition-colors ${value === s ? (s === "published" ? "bg-teal text-offwhite" : "bg-midnight text-offwhite") : "text-midnight/70 hover:text-midnight"}`}
        >
          {s === "published" ? "Published" : "Draft"}
        </button>
      ))}
    </div>
  );
}

export function Badge({ status }: { status: "published" | "draft" }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 font-mono text-[0.65rem] tracking-[0.15em] uppercase ${status === "published" ? "bg-teal/15 text-teal" : "bg-midnight/10 text-midnight/70"}`}>
      {status === "published" ? "Live" : "Draft"}
    </span>
  );
}

/** A list of short lines (e.g. "What's included", heading lines), with add, remove and reorder. */
export function LinesEditor({ label, hint, value, onChange, placeholder, addLabel = "Add line", lang }: {
  label: string;
  lang?: EditLang;
  hint?: ReactNode;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  addLabel?: string;
}) {
  const set = (i: number, v: string) => onChange(value.map((x, j) => (j === i ? v : x)));
  const move = (i: number, by: number) => {
    const next = [...value];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item);
    onChange(next);
  };
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1.5 text-sm font-bold">{label}</legend>
      {value.map((line, i) => (
        <div key={i} className="flex items-center gap-2">
          <input aria-label={`${label} ${i + 1}`} value={line} placeholder={placeholder} onChange={(e) => set(i, e.target.value)} className={INPUT} {...(lang && langProps(lang))} />
          <IconButton label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</IconButton>
          <IconButton label="Move down" disabled={i === value.length - 1} onClick={() => move(i, 1)}>↓</IconButton>
          <IconButton label="Remove" onClick={() => onChange(value.filter((_, j) => j !== i))}>✕</IconButton>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, ""])} className={`${BTN_GHOST} self-start`}>
        + {addLabel}
      </button>
      {hint && <p className="text-sm text-midnight/70">{hint}</p>}
    </fieldset>
  );
}

export function IconButton({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-11 shrink-0 place-items-center rounded-lg border border-midnight/15 text-midnight/80 transition-colors hover:border-teal hover:text-teal disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/** A delete button that asks for confirmation first. */
export function ConfirmButton({ label = "Delete", question, onConfirm, className = BTN_DANGER, confirmLabel = "Yes, delete", confirmClassName = BTN_DANGER }: { label?: string; question: string; onConfirm: () => void; className?: string; confirmLabel?: string; confirmClassName?: string }) {
  const [asking, setAsking] = useState(false);
  if (!asking)
    return (
      <button type="button" onClick={() => setAsking(true)} className={className}>
        {label}
      </button>
    );
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="text-sm font-bold">{question}</span>
      <button type="button" onClick={() => { setAsking(false); onConfirm(); }} className={confirmClassName}>
        {confirmLabel}
      </button>
      <button type="button" onClick={() => setAsking(false)} className={BTN_GHOST}>
        Cancel
      </button>
    </span>
  );
}

/** Save result message, announced to screen readers. */
export function Notice({ notice }: { notice: { type: "ok" | "error"; text: string } | null }) {
  return (
    <p role="status" aria-live="polite" className={notice ? `rounded-lg px-4 py-3 text-sm font-bold ${notice.type === "ok" ? "bg-teal/15 text-teal" : "bg-red-700/10 text-red-800"}` : "sr-only"}>
      {notice?.text ?? ""}
    </p>
  );
}

export function PageTitle({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export const today = () => new Date().toISOString().slice(0, 10);
